/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unused-prop-types */
/* eslint-disable camelcase */
/* eslint-disable no-nested-ternary */
import React, {
  Suspense, useMemo, useCallback, useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  ThemeProvider, ScopedCssBaseline, styled,
} from '@mui/material';
import moment from 'moment';
import {
  KrMilestonesList,
  KrMilestonesListProps,
  Milestone,
  KrMilestoneContextValue,
} from '@workboard/ui-okr';
import { GlobalStyle, theme } from './theme/theme';

const StyledCssBaseline = styled(ScopedCssBaseline)(
  {
    'body #new-kr-milestones-area &, body #edit-kr-milestones-area &': {
      '& label': {
        top: '-10px',
        left: '-13px',
      },
    },
  },
);

export type KrMilestonesProps = {
  /* use to explicitly set the source of the metric */
  source?: string;
  mitChildKr?: boolean;
  isChildKr?: boolean;
  metric: {
    milestones: { id: string; date: number; label: string; target: number }[],
    source: string,
    start_date: number | string,
    end_date: number | string,
    initial_data: number | string,
    target_raw: number | string,
    counting?: string;
    weekly_update_day?: string;
  };
  goalDetails: {
    start_date: number | string;
    target_date: number | string;
  };
  // live state of the metric
  metricState: {
    initial_data?: number | null;
    target?: number | null;
    stretch?: number | null;
    source?: string;
    dsMetric?: number;
    dayType?: string;
    // rollup metric object
    metricObject?: {
      initial_data?: string | null;
      target?: string | null;
      target_raw?: string | null;
      stretch?: string | null;
      source?: string;
      counting?: string;
    };
    metricObj?: {
      initial_data?: string | null;
      target?: string | null;
      target_raw?: string | null;
      stretch?: string | null;
      source?: string;
      counting?: string;
    };
    krObj?: {
      initial_data?: string | null;
      target?: string | null;
      target_raw?: string | null;
      stretch?: string | null;
      source?: string;
      counting?: string;
    };
  };
  onChange?: KrMilestonesListProps['onChange'];
  disabled?: KrMilestonesListProps['disabled'];
  kr_duration?: {
    start: number | string;
    end: number | string;
  };
};

const getGoalStartAndTargetDate = (
  goalDetails: { start_date: number | string; target_date: number | string }
) => {
  // goal should always have a start and target date but just in case data is not present
  if (!goalDetails?.start_date || !goalDetails?.target_date) {
    return { start: undefined, end: undefined };
  }
  return {
    goalStart:
      typeof goalDetails.start_date === 'number'
        ? goalDetails.start_date
        : moment.utc(goalDetails.start_date).unix(),
    goalTarget:
      typeof goalDetails.target_date === 'number'
        ? goalDetails.target_date
        : moment.utc(goalDetails.target_date).unix(),
  };
};

const NumberOrNull = (value: number | string | null) => {
  const number = parseFloat(String(value));
  return !Number.isNaN(number) ? number : null;
};

const DateOrNull = (value: number | string | null) => {
  const date = value && moment.utc(value);
  return date?.isValid?.() ? date.unix() : null;
};
const getKeyResultData = (params: KrMilestonesProps) => {
  const { metric, metricState, kr_duration } = params;
  const source =
    metricState?.krObj?.source ??
    metricState?.metricObj?.source ??
    metricState?.metricObject?.source ??
    metricState?.source ??
    metric?.source;
  const milestones = metric?.milestones;
  const startDate =
    DateOrNull(kr_duration?.start) ??
    NumberOrNull(metric?.start_date) ??
    metric?.start_date;
  const endDate =
    DateOrNull(kr_duration?.end) ??
    NumberOrNull(metric?.end_date) ??
    metric?.end_date;
  const initialData =
    NumberOrNull(metricState?.krObj?.initial_data) ??
    NumberOrNull(metricState?.metricObj?.initial_data) ??
    NumberOrNull(metricState?.metricObject?.initial_data) ??
    NumberOrNull(metricState?.initial_data) ??
    NumberOrNull(metric?.initial_data);
  const target =
    NumberOrNull(metricState?.krObj?.target) ??
    NumberOrNull(metricState?.krObj?.stretch) ??
    NumberOrNull(metricState?.metricObj?.target) ??
    NumberOrNull(metricState?.metricObj?.stretch) ??
    NumberOrNull(metricState?.metricObject?.target) ??
    NumberOrNull(metricState?.metricObject?.stretch) ??
    NumberOrNull(metricState?.target) ??
    NumberOrNull(metricState?.stretch) ??
    NumberOrNull(metric?.target_raw);
  const weeklyUpdateDay =
    NumberOrNull(metricState?.dayType) ??
    NumberOrNull(metric?.weekly_update_day);

  // metric should always have a start and end date but just in case data is not present
  const startDateUnix = startDate
    ? typeof startDate === 'number'
      ? startDate
      : moment.utc(startDate).unix()
    : undefined;
  const endDateUnix = endDate
    ? typeof endDate === 'number'
      ? endDate
      : moment.utc(endDate).unix()
    : undefined;

  return {
    source,
    milestones,
    startDate: startDateUnix,
    endDate: endDateUnix,
    startValue: initialData ?? 0,
    targetValue: target ?? 100,
    weeklyUpdateDay,
  };
};

const KrMilestones = forwardRef((
  props: KrMilestonesProps,
  ref: React.Ref<KrMilestoneContextValue>
) => {
  const {
    metric,
    goalDetails,
    onChange,
    disabled,
  } = props;

  const { goalStart, goalTarget } = getGoalStartAndTargetDate(goalDetails);
  const {
    source,
    milestones,
    startDate,
    endDate,
    startValue,
    targetValue,
    weeklyUpdateDay,
  } = getKeyResultData(props);

  const milestonesListRef = useRef<KrMilestoneContextValue>(null);
  const kr = useMemo(
    () => ({
      source,
      startDate: startDate ?? goalStart,
      endDate: endDate ?? goalTarget,
      startValue,
      targetValue,
      milestones,
      weeklyUpdateDay
    }),
    [
      goalStart,
      goalTarget,
      startDate,
      endDate,
      startValue,
      targetValue,
      source,
      milestones,
      weeklyUpdateDay,
    ],
  );

  const handleMilestonesChange = useCallback(
    (newMilestones: Milestone[]) => {
      onChange?.(newMilestones);
    },
    [onChange],
  );

  /**
    miltones update when Kr is changed, but no onChange is called,
    so we trigger it manually to keep the milestones in sync when
    kr startDate/endDate/startValue/targetValue... is changed
   */
  useEffect(() => {
    if (milestonesListRef.current) {
      onChange?.(milestonesListRef.current.milestones);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kr]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(ref, () => milestonesListRef.current, [milestonesListRef.current]);

  if (!metric) {
    return null;
  }

  return (
    <StyledCssBaseline>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <style>
          {`
            .highcharts-tooltip-container {
              max-width: unset;
              max-height: unset;
              overflow: visible;
              z-index: 9999999999 !important;
            }
            .highcharts-tooltip-container .highcharts-root,
            .highcharts-tooltip-container .highcharts-tooltip {
              pointer-events: none !important;
            }
              
            .kr-milestones-container .MuiInputBase-root {
              border-radius: 4px;
              border: 1px solid rgba(0, 0, 0, .06);
              background-color: #f2f2f2;
              height: 37px;
              padding: 0;
            }
              
            .kr-milestones-container .KrMilestonesButtonGroup button,
            .kr-milestones-container .KrMilestonesAddMilestoneButton {
              /* server styles override MUI text button color in the KR edit wizard */
              color: #0072ab !important;
            }
          `}
        </style>
        <div className="kr-milestones-container">
          <Suspense fallback="Loading Key Result Milestones...">
            <KrMilestonesList
              ref={milestonesListRef}
              kr={kr}
              disabled={disabled}
              onChange={handleMilestonesChange}
            />
          </Suspense>
        </div>
      </ThemeProvider>
    </StyledCssBaseline>
  );
});

KrMilestones.defaultProps = {
  onChange: undefined,
};

export default KrMilestones;
